import AddToDashboardButton from './AddToDashboardButton'
import AnalysisCardTitle from './AnalysisCardTitle'
import ResearchVisualization from '../dataVisualizations/ResearchVisualization'
import ParentCompareView from '../dataVisualizations/ParentCompareView'
import periodRangeToDates from '../../helpers/liveReportHelpers/periodRangeToDates'
import { EmptyStateProvider } from '../../StateManagment/EmptyStateContext'

// AI-CONTEXT: See docs/features/livereport-widgets-dashboard-parity.md
// AI-CONTEXT: See docs/features/compare-two-periods.md
// Thin wrapper around `ResearchVisualization` — adds the dashboard-specific
// header and the per-question pin button. All visualisation logic (filter bar,
// navigator, ResearchData) lives in the shared component.
//
// Compare mode (compareActive + rangeA/rangeB): header once + one shared
// navigator across both periods, via ParentCompareView.

// Analyse/dashboard cards show the empty-state CTA in their charts.
const EMPTY_STATE_CTX = { type: 'research', showCta: true }

/**
 * @param {object} props
 * @param {object} props.research
 * @param {string} [props.titleLink] - Path to the deep analysis; makes the title a link (Analyse page only)
 * @param {boolean} [props.compareActive] - Render two periods side by side
 * @param {{start: string|null, end: string|null}|null} [props.rangeA] - Primary period (also the single-period filter)
 * @param {{start: string|null, end: string|null}|null} [props.rangeB] - Compare period
 */
const AnalysisResearchCardBody = ({
    research,
    headerAction,
    titleLink,
    graphTypeOverride,
    colorPalette,
    colorOverrides,
    personas,
    personasEnabled,
    selectedPersonaId,
    onPersonaChange,
    showOptions = false,
    graphTypeByIndicator,
    colorOverridesByIndicator,
    onSaveIndicatorOptions,
    hidePins = false,
    compareActive = false,
    rangeA = null,
    rangeB = null,
}) => {
    // Analyse page: synthesise a DashboardItems-shaped object from the card's
    // transient option state; the graph-type / colour picker reads the same map
    // fields the dashboard doc uses and saves back via onSaveOptions.
    const optionsItem = showOptions
        ? {
            GraphTypeByIndicator: graphTypeByIndicator || {},
            ColorOverridesByIndicator: colorOverridesByIndicator || {},
        }
        : null

    // Deep-analysis page (hidePins): the page's anchor header shows the title
    // and its context card the description, so no card header there.
    const headerEl = !hidePins ? (
        <div className="new-dashboard-card-label analysis-card-header">
            <div className="analysis-card-header-text">
                <AnalysisCardTitle title={research.Title} titleLink={titleLink} />
                {research.Description && (
                    <p className="analysis-card-description">{research.Description}</p>
                )}
            </div>
            {headerAction || (
                <AddToDashboardButton
                    type='research'
                    researchId={research.ID}
                    label={research.Title}
                    showLabel
                />
            )}
        </div>
    ) : null

    const renderViz = (range, nav) => {
        const { startDate, endDate } = periodRangeToDates(range)
        const compare = !!nav
        return (
            <ResearchVisualization
                research={research}
                graphTypeOverride={graphTypeOverride}
                colorPalette={colorPalette}
                colorOverrides={colorOverrides}
                personas={personas}
                personasEnabled={personasEnabled}
                selectedPersonaId={selectedPersonaId}
                onPersonaChange={onPersonaChange}
                dashboardItem={optionsItem}
                onSaveOptions={showOptions ? onSaveIndicatorOptions : undefined}
                startDate={startDate}
                endDate={endDate}
                periodRange={range}
                hideControls={compare}
                visible={compare ? nav.visible : undefined}
                onVisibleChange={compare ? nav.setVisible : undefined}
                onItemsChange={compare ? nav.setItems : undefined}
                renderItemAction={(hidePins || compare) ? undefined : (currentField, currentResearch) => (
                    <AddToDashboardButton
                        type='indicator'
                        researchId={currentResearch.ID}
                        indicatorId={currentField.ID}
                        label={currentField.Question || 'Vraag'}
                    />
                )}
            />
        )
    }

    return (
        <EmptyStateProvider value={EMPTY_STATE_CTX}>
            {compareActive ? (
                <ParentCompareView
                    header={headerEl}
                    rangeA={rangeA}
                    rangeB={rangeB}
                    renderViz={(range, side, nav) => renderViz(range, nav)}
                />
            ) : (
                <>
                    {headerEl}
                    {renderViz(rangeA, null)}
                </>
            )}
        </EmptyStateProvider>
    )
}

export default AnalysisResearchCardBody
